import { Suspense } from 'react';
import { BrowserRouter } from 'react-router-dom';

/**
 * Router shell (League SDK 3 migration guide):
 * The guide illustrates `createBrowserRouter` + `RouterProvider` so a **data router**
 * wraps the subtree that mounts `LeagueProvider`. This app uses **React Router v6
 * `BrowserRouter`**, which still provides the **router context** (`useNavigate`, `useLocation`,
 * etc.) that `core.routing.adapter: createReactRouterAdapter()` relies on.
 *
 * Migrating to `RouterProvider` would mean lifting the entire route tree from
 * `app-routes.component.tsx` into `createBrowserRouter` / `createRoutesFromElements` —
 * same semantics, larger refactor. Revisit if League/extensions require a data router API.
 */

import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  ErrorState,
  ERROR_TYPES,
} from '@telus-alberta-health-web/shared-module';
import { useOnlineStatus } from '@leagueplatform/web-common';
import { LoadingIndicator } from '@leagueplatform/web-common-components';
import League from '@leagueplatform/core';
import { ObservabilityErrorBoundary } from '@leagueplatform/observability';
import AppRoutes from 'components/routes/app-routes.component';
import { config, routesConfig } from 'config';
import { initMasonryRegistry } from 'init-masonry-registry';
import { QueryClientProviderWrapper } from 'client-provider-wrapper';
import { useBfcacheReload } from './hooks/use-bf-cache-reload';

League.overwriteRoutePaths(routesConfig);

/** Register custom Masonry node renderers on the v3 `driver` before any `<Masonry />` renders. */
initMasonryRegistry();

export function App() {
  useBfcacheReload();

  return (
    <BrowserRouter>
      <QueryClientProviderWrapper>
        <League.LeagueProvider config={config}>
          <ObservabilityErrorBoundary
            fallback={
              <ErrorState
                isFullPage
                errorType={ERROR_TYPES.API_ERROR}
                error={null}
              />
            }
            errorContext={{
              context: { Context: { location: 'Root App ErrorBoundary' } },
            }}
          >
            <Suspense fallback={<LoadingIndicator />}>
              {useOnlineStatus().isOffline && (
                <ErrorState
                  isFullPage
                  errorType={ERROR_TYPES.NO_CONNECTION}
                  hideButton
                  error={null}
                />
              )}
              {useOnlineStatus().isOnline && <AppRoutes />}
              <ReactQueryDevtools />
            </Suspense>
          </ObservabilityErrorBoundary>
        </League.LeagueProvider>
      </QueryClientProviderWrapper>
    </BrowserRouter>
  );
}
